import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import './FrequencyChart.scss';

// import WaveChart library
import WaveCharts from 'wave-charts';
import WaveTheme from 'wave-charts/themes/WaveTheme.json';
import 'wave-charts/wave-charts.css';

// import WaveChart react component
import WaveChart from 'wave-charts/components/wave-chart';
import 'wave-charts/components/wave-chart/index.css';

import { chartConfig, colorsConfig } from '../data/rowsConfig';

import ComboChart, { getMetadata } from 'wave-charts/modules/sfdc-combo-chart';
WaveCharts.__getPrivateApi().registerModule({
  default: ComboChart,
  getMetadata
});

WaveCharts.registerTheme('wave', WaveTheme);

export class FrequencyChart extends Component {
  generateLegendItems() {
    const { formatMessage } = this.props.intl;
    const { frequencies } = this.props;
    const legendBarMap = {
      none: {
        label: formatMessage({ id: 'FrequencyChart.labels.none' }),
        figureType: 'bar',
        colorStyle: 'none'
      },
      under: {
        label: formatMessage({ id: 'FrequencyChart.labels.undersaturated' }),
        figureType: 'bar',
        colorStyle: 'under'
      },
      neutral: {
        label: formatMessage({ id: 'FrequencyChart.labels.neutral' }),
        figureType: 'bar',
        colorStyle: 'neutral'
      },
      over: {
        label: formatMessage({ id: 'FrequencyChart.labels.oversaturated' }),
        figureType: 'bar',
        colorStyle: 'over'
      },
      excluded: {
        label: formatMessage({ id: 'FrequencyChart.labels.excluded' }),
        figureType: 'bar',
        colorStyle: 'excluded'
      }
    };
    const saturationTypes = [
      ...new Set(frequencies.map(frequency => frequency.saturationType))
    ];
    let legendItems = [
      {
        label: formatMessage({ id: 'FrequencyChart.labels.openRate' }),
        figureType: 'line',
        colorStyle: 'open'
      },
      {
        label: formatMessage({ id: 'FrequencyChart.labels.clickRate' }),
        figureType: 'line',
        colorStyle: 'click'
      },
      {
        label: formatMessage({ id: 'FrequencyChart.labels.unsubscribeRate' }),
        figureType: 'line',
        colorStyle: 'unsubscribe'
      }
    ];
    saturationTypes
      .reverse()
      .map(type => legendItems.unshift(legendBarMap[type]));
    return legendItems;
  }

  render() {
    const { formatMessage } = this.props.intl;

    const rowsMetaData = [
      {
        column: 'numEmail',
        type: 'dimension',
        label: formatMessage({ id: 'FrequencyChart.labels.emailFrequency' })
      },
      {
        column: 'subscribers',
        type: 'measure',
        label: formatMessage({ id: 'FrequencyChart.labels.subscribers' }),
        format: ['###,##', 1]
      },
      {
        column: 'openRate',
        type: 'measure',
        label: formatMessage({ id: 'FrequencyChart.labels.openRate' }),
        format: ['###.##%', 100]
      },
      {
        column: 'clickRate',
        type: 'measure',
        label: formatMessage({ id: 'FrequencyChart.labels.clickRate' }),
        format: ['###.##%', 100]
      },
      {
        column: 'unsubscribeRate',
        type: 'measure',
        label: formatMessage({ id: 'FrequencyChart.labels.unsubscribeRate' }),
        format: ['###.##%', 100]
      },
      {
        column: 'saturationType',
        type: 'dimension',
        label: formatMessage({ id: 'FrequencyChart.labels.impressions' }),
        valueLabels: {
          under: formatMessage({ id: 'FrequencyChart.labels.undersaturated' }),
          neutral: formatMessage({ id: 'FrequencyChart.labels.neutral' }),
          over: formatMessage({ id: 'FrequencyChart.labels.oversaturated' })
        },
        valueColors: colorsConfig
      }
    ];
    const legendItems = this.generateLegendItems();

    //configure chart appearance
    const config = {
      ...chartConfig,
      autoFitMode: this.props.scrollable ? 'scroll' : chartConfig.autoFitMode,
      measureAxis2: {
        title: formatMessage({ id: 'FrequencyChart.labels.engagementRate' })
      },
      barSpacer:
        this.props.visualMode === FrequencyChart.visualModes.expanded
          ? chartConfig.barSpacer + 2
          : chartConfig.barSpacer
    };

    return (
      <div className={`frequency-chart ${this.props.visualMode}`}>
        <WaveChart
          className="wave-chart-component"
          chartModule="sfdc-combo-chart"
          rows={this.props.frequencies}
          config={config}
          rowsMetadata={rowsMetaData}
        />
        <ul className="frequency-chart-legend">
          {legendItems.map(item => {
            return (
              <li
                className={`${item.figureType}-indicator ${item.colorStyle}`}
                key={item.label}
              >
                {item.label}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  static get visualModes() {
    return {
      standard: 'standard',
      expanded: 'expanded'
    };
  }
}

FrequencyChart.propTypes = {
  children: PropTypes.element,
  location: PropTypes.object,
  frequencies: PropTypes.array.isRequired,
  chartParams: PropTypes.object,
  scrollable: PropTypes.bool,
  visualModes: PropTypes.oneOf(Object.keys(FrequencyChart.visualModes)),
  intl: intlShape.isRequired
};

FrequencyChart.defaultProps = {
  scrollable: false,
  visualModes: FrequencyChart.visualModes.standard
};

export default injectIntl(FrequencyChart);
